import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_SMS_FORM,
  buildSmsFormFromProvider,
  formToSmsPayload,
  getSmsModalTitle,
  rowToSmsForm,
  validateSmsForm,
  isSecretSmsField,
} from './smsManageConstants';

function StatusRadio({ value, onChange }) {
  return (
    <div className="radio-row radio-row--inline">
      <label>
        <input
          type="radio"
          name="smsEnabled"
          checked={value === true}
          onChange={() => onChange(true)}
        />
        開
      </label>
      <label>
        <input
          type="radio"
          name="smsEnabled"
          checked={value === false}
          onChange={() => onChange(false)}
        />
        關
      </label>
    </div>
  );
}

export default function SmsConfigEditModal({
  open,
  row,
  fieldLabels,
  providerNameMap,
  providerOptions,
  onClose,
  onSubmit,
  submitting,
}) {
  const isEdit = Boolean(row?.providerKey);
  const [form, setForm] = useState(DEFAULT_SMS_FORM);
  const [initialForm, setInitialForm] = useState(DEFAULT_SMS_FORM);
  const [error, setError] = useState('');

  const modalTitle = useMemo(
    () => getSmsModalTitle(isEdit, form.providerKey, providerNameMap),
    [isEdit, form.providerKey, providerNameMap]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    let nextForm = DEFAULT_SMS_FORM;
    if (isEdit) {
      nextForm = rowToSmsForm(row, fieldLabels);
    } else {
      const first = providerOptions?.[0];
      const providerKey = first?.providerKey || '';
      nextForm = buildSmsFormFromProvider(providerKey, fieldLabels, false);
    }
    setForm(nextForm);
    setInitialForm(nextForm);
    setError('');
  }, [open, row?.providerKey, isEdit, fieldLabels, providerOptions]);

  if (!open) {
    return null;
  }

  const labels = fieldLabels[form.providerKey] || {};

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateContentField(key, value) {
    setForm((prev) => ({
      ...prev,
      contentFields: { ...prev.contentFields, [key]: value },
    }));
  }

  function handleProviderChange(providerKey) {
    setForm(buildSmsFormFromProvider(providerKey, fieldLabels, form.enabled));
  }

  function handleReset() {
    setForm({ ...initialForm, contentFields: { ...initialForm.contentFields } });
    setError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validateSmsForm(form, fieldLabels);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    try {
      await onSubmit(formToSmsPayload(form), isEdit);
    } catch {
      // 錯誤由父層處理
    }
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--sms"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{modalTitle}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body">
            {error ? <p className="form-message form-message--error">{error}</p> : null}
            <div className="sms-modal-form">
              {!isEdit ? (
                <div className="sms-form-row">
                  <label className="sms-form-row__label" htmlFor="smsProviderKey">
                    <span className="form-label-required">短信商名稱</span>
                  </label>
                  <div className="sms-form-row__control">
                    <select
                      id="smsProviderKey"
                      value={form.providerKey}
                      onChange={(e) => handleProviderChange(e.target.value)}
                    >
                      {providerOptions.map((opt) => (
                        <option key={opt.providerKey} value={opt.providerKey}>
                          {opt.providerName || opt.providerKey}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}

              <div className="sms-form-row">
                <span className="sms-form-row__label">狀態</span>
                <div className="sms-form-row__control">
                  <StatusRadio
                    value={form.enabled}
                    onChange={(enabled) => updateField('enabled', enabled)}
                  />
                </div>
              </div>

              {Object.entries(labels).map(([key, label]) => (
                <div key={key} className="sms-form-row">
                  <label className="sms-form-row__label" htmlFor={`smsField-${key}`}>
                    {label || key}
                  </label>
                  <div className="sms-form-row__control">
                    <input
                      id={`smsField-${key}`}
                      type={isSecretSmsField(key) ? 'password' : 'text'}
                      autoComplete="off"
                      value={form.contentFields[key] ?? ''}
                      onChange={(e) => updateContentField(key, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
